import React, { useContext, useState, useEffect } from 'react';
import {
  JsonHubProtocol,
  HubConnectionBuilder,
  HubConnection,
  HttpTransportType,
  LogLevel,
} from '@aspnet/signalr';
import { useAuth0 } from '@cmr/react-auth0';

interface ISignalrContext {
  connection?: HubConnection;
}

export const SignalRContext = React.createContext<ISignalrContext>({});
export const useSignalR = () => useContext(SignalRContext);

interface SignalRProviderOptions {
  children: React.ReactElement;
  negotiateEndpoint: string;
}

export const SignalRProvider = ({
  children,
  negotiateEndpoint,
}: SignalRProviderOptions) => {
  const [connection, setConnection] = useState<HubConnection>();
  const { user, getTokenSilently, isAuthenticated, loading } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated || loading) return;

    const initSignalR = async () => {
      const connectionHubUrl = `${negotiateEndpoint}?userId=${user.email.toLowerCase()}`;

      const protocol = new JsonHubProtocol();
      const token = await getTokenSilently();

      if (!token) return;

      const options = {
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
        logMessageContent: true,
        logger: LogLevel.Trace,
        accessTokenFactory: () => token,
      };

      const newConnection = new HubConnectionBuilder()
        .withUrl(connectionHubUrl, options)
        .withHubProtocol(protocol)
        .build();

      newConnection.onclose(() =>
        setTimeout(() => newConnection.start(), 5000),
      );

      newConnection.start();
      setConnection(newConnection);
    };
    initSignalR();
  }, [user, isAuthenticated, getTokenSilently, loading, negotiateEndpoint]);

  return (
    <SignalRContext.Provider value={{ connection }}>
      {children}
    </SignalRContext.Provider>
  );
};
